import { isObject, escapePointer } from "./json.js";

// A Custom chart is untrusted JSON rendered inside the app, so it may only
// draw what the host hands it. Static checks here give the author a precise
// error at the offending path; the runtime loader (loader.ts) enforces the
// same rule for anything that slips past, so neither alone is load-bearing.

const REMOTE = "loading remote data or images is not allowed";
const LINK = "links are not allowed in charts";
const BIND = "binding controls outside the chart is not allowed";

/**
 * Finds every property that would reach outside the chart: a `url` (remote
 * data, topojson, or an image mark's source), an `href` (click-through
 * navigation), and a signal binding's `element` (a CSS selector that would
 * inject controls into the page). Returns one message per hit, prefixed with
 * its JSON pointer. Holds for both grammars — the keys mean the same in each.
 */
export function findUnsafeProperties(spec: unknown): string[] {
  const problems: string[] = [];
  const visit = (node: unknown, path: string, parentKey: string | null) => {
    if (Array.isArray(node)) {
      node.forEach((child, index) => visit(child, `${path}/${index}`, null));
      return;
    }
    if (!isObject(node)) return;
    for (const [key, value] of Object.entries(node)) {
      // Inline rows are data, not spec: a column named `url` is harmless.
      if (key === "values") continue;
      const at = `${path}/${escapePointer(key)}`;
      if (key === "url") problems.push(`${at}: ${REMOTE}`);
      else if (key === "href") problems.push(`${at}: ${LINK}`);
      else if (key === "element" && parentKey === "bind") {
        problems.push(`${at}: ${BIND}`);
      } else visit(value, at, key);
    }
  };
  visit(spec, "", null);
  return problems;
}
